import React, { useCallback } from 'react';
import { graphql } from 'gatsby';
import qs from 'qs';
import { Configure, InstantSearch } from 'react-instantsearch-dom';
import { connectSearchBox } from 'react-instantsearch-dom';

import Layout from '../components/Layout';
import SEO from '../components/seo';
import TutorialBanners from '../components/tutorial/search/TutorialBanners';
import TutorialHits from '../components/tutorial/search/TutorialsHits';
import TutorialToolbar from '../components/tutorial/search/TutorialToolbar';
import { searchClient } from '../hooks/useAlgolia';

import './tutorials.scss';
const VirtualSearchBox = connectSearchBox(() => null);

function createURL(searchState) {
  return qs.stringify({ query: searchState.query }, { addQueryPrefix: true });
}

function searchStateToUrl({ location }, searchState) {
  if (Object.keys(searchState).length === 0) {
    return '';
  }

  return `${location.pathname}${createURL(searchState)}`;
}

function urlToSearchState({ search }) {
  return qs.parse(search.slice(1));
}

export default function Tutorials({ data }: { data: any }): React.ReactElement {
  // list of products to retrieve the correct name for each product slug, it will be used for the product select filter
  const products = data.products.edges.map((p) => ({
    value: p.node.fields.slug?.split('/')[2],
    name: p.node.childMdx.frontmatter.title,
  }));

  // list of sorted hero tutorials that will be used for the carousel
  const heroBannerTutorials = data.heroTutorials.edges
    .map((t) => ({
      title: t.node.childMdx.frontmatter.title,
      description: t.node.childMdx.frontmatter.description,
      slug: t.node.fields.slug,
      heroImage: data.heroImages.edges.find((img) => img.node.relativeDirectory === t.node.relativeDirectory)?.node,
      position: t.node.childMdx.frontmatter.hero_position,
    }))
    .sort((a, b) => (a.position <= b.position ? -1 : 1));

  // Check if window is defined (so if in the browser or in node.js).
  const isBrowser = typeof window !== 'undefined';

  const [searchState, setSearchState] = React.useState(() => urlToSearchState(isBrowser ? window.location : { search: '' }));
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      window.history.pushState(searchState, null, searchStateToUrl({ location: window.location }, searchState));
    }, 400);
  }, [searchState]);

  const onSubmit = useCallback(({ state }) => {
    setSearchState((searchState) => ({
      ...searchState,
      query: state.query,
    }));
  }, []);

  const onReset = useCallback(() => {
    setSearchState((searchState) => ({
      ...searchState,
      query: '',
    }));
  }, []);

  return (
    <Layout>
      <SEO title="Tutorials" description="Arduino featured tutorials" />
      <TutorialBanners banners={heroBannerTutorials} />
      <InstantSearch
        indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME}
        searchClient={searchClient}
        searchState={searchState}
        onSearchStateChange={setSearchState}
        createURL={createURL}
      >
        <Configure filters="documentation_type:'Tutorials'" />
        <VirtualSearchBox />
        <TutorialToolbar products={products} onReset={onReset} onSubmit={onSubmit} searchState={searchState} />
        <div className="contentWrapper">
          <div className="content">
            <TutorialHits />
          </div>
        </div>
      </InstantSearch>
    </Layout>
  );
}

export const pageQuery = graphql`
  query {
    products: allFile(filter: { base: { eq: "product.md" }, relativeDirectory: { regex: "/^hardware/" } }) {
      edges {
        node {
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
            }
          }
        }
      }
    }
    heroTutorials: allFile(filter: { childMdx: { frontmatter: { hero_position: { gte: 0 } } } }) {
      edges {
        node {
          relativeDirectory
          fields {
            slug
          }
          childMdx {
            frontmatter {
              title
              description
              hero_position
            }
          }
        }
      }
    }
    heroImages: allFile(filter: { base: {regex: "/\\hero-banner.(jpe?g|png|gif|bmp|svg)$/i"}}) {
      edges {
        node {
          relativeDirectory
          childImageSharp {
            gatsbyImageData(quality: 90, placeholder: BLURRED)
          }
          extension
          publicURL
        }
      }
    }
  }
`;
